import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSnackbar } from './SnackbarProvider';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import routes from '../config/routes';

// Context-aware session configuration
const getSessionConfig = (userType, federationMode) => {
  let timeout;
  
  // Determine timeout based on user type and federation mode
  if (federationMode === 'demo') {
    timeout = 24 * 60 * 60 * 1000; // 24 hours for demo mode
  } else if (userType === 'patient') {
    timeout = 30 * 60 * 1000; // 30 minutes for patients (HIPAA requirement)
  } else if (userType === 'admin') {
    timeout = 120 * 60 * 1000; // 120 minutes for admins
  } else {
    // Default 60 minutes for ASC/hospital staff
    timeout = 60 * 60 * 1000;
  }
  
  return {
    IDLE_TIMEOUT: timeout,
    WARNING_TIME: 5 * 60 * 1000, // 5 minute warning
    FINAL_WARNING: 60 * 1000, // 1 minute final warning
    ACTIVITY_EVENTS: ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'],
  };
};

const getAuthToken = () => localStorage.getItem('_at') || localStorage.getItem('token');

export default function SessionMonitor({ onSessionExpired }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user, logout } = useEnterpriseAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [warningLevel, setWarningLevel] = useState(null); // 'first', 'second', 'final'
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const scheduledTimeoutsRef = useRef({
    first: null,
    second: null,
    final: null,
    expire: null,
  });
  const updateActivityRef = useRef(null);
  const scheduleSessionTimersRef = useRef(null);
  const sessionConfigRef = useRef(null);

  // Get session configuration based on user context
  useEffect(() => {
    if (user) {
      const userType = user.userType || (user.type === 'patient' ? 'patient' : 'staff');
      const federationMode = user.federation?.mode || user.mode || 'hospital';
      sessionConfigRef.current = getSessionConfig(userType, federationMode);
      setTimeRemaining(sessionConfigRef.current.IDLE_TIMEOUT);
      
      // Get or create session ID from storage or auth response
      const storedSessionId = sessionStorage.getItem('sessionId') || user.sessionId;
      if (storedSessionId) {
        setSessionId(storedSessionId);
      }
    }
  }, [user]);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If warning is showing, hide it and reset timer
    if (showWarning) {
      setShowWarning(false);
      setWarningLevel(null);
      clearInterval(warningTimerRef.current);
    }

    // Log activity to backend (throttled)
    if (sessionId) {
      const now = Date.now();
      const lastLog = parseInt(sessionStorage.getItem('lastActivityLog') || '0');
      
      // Only log if more than 30 seconds since last log
      if (now - lastLog > 30000) {
        sessionStorage.setItem('lastActivityLog', now.toString());
        
        fetch('/api/sessions/activity', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            sessionId,
            activityType: 'activity',
            details: {
              type: 'user_interaction',
              timestamp: new Date().toISOString(),
            },
          }),
        }).catch(error => {
          console.error('Failed to log activity:', error);
        });
      }
    }

    scheduleSessionTimersRef.current?.();
  }, [showWarning, sessionId]);

  useEffect(() => {
    updateActivityRef.current = updateActivity;
  }, [updateActivity]);

  // Extend session
  const extendSession = useCallback(async () => {
    if (!sessionId) {
      showSnackbar('No active session found', 'error');
      return;
    }

    try {
      const response = await fetch('/api/sessions/extend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ sessionId }),
      });

      if (response.ok) {
        const data = await response.json();
        updateActivity();
        setTimeRemaining(sessionConfigRef.current?.IDLE_TIMEOUT || 60 * 60 * 1000);
        showSnackbar('Session extended successfully', 'success');
        
        // Log session extension
        fetch('/api/sessions/activity', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            sessionId,
            activityType: 'extend',
            details: {
              extendedAt: new Date().toISOString(),
              newExpiry: data.data?.expiresAt,
            },
          }),
        }).catch(console.error);
      } else {
        throw new Error('Failed to extend session');
      }
    } catch (error) {
      console.error('Error extending session:', error);
      showSnackbar('Failed to extend session', 'error');
      handleSessionExpired();
    }
  }, [sessionId, updateActivity, showSnackbar]);

  // Handle session expiry
  const handleSessionExpired = useCallback(async () => {
    setShowWarning(false);
    setWarningLevel(null);
    clearInterval(warningTimerRef.current);

    // Clear any scheduled timers
    Object.entries(scheduledTimeoutsRef.current).forEach(([key, timer]) => {
      if (timer) {
        clearTimeout(timer);
        scheduledTimeoutsRef.current[key] = null;
      }
    });

    // Remove event listeners
    if (sessionConfigRef.current) {
      const activityHandler = updateActivityRef.current;
      sessionConfigRef.current.ACTIVITY_EVENTS.forEach(event => {
        if (activityHandler) {
          document.removeEventListener(event, activityHandler);
        }
      });
    }

    // Terminate session on backend
    if (sessionId) {
      try {
        await fetch('/api/sessions/terminate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            sessionId,
            reason: 'timeout',
          }),
        });
      } catch (error) {
        console.error('Failed to terminate session:', error);
      }
    }

    // Clear session data
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('lastActivityLog');

    // Call parent handler if provided
    if (onSessionExpired) {
      onSessionExpired();
    }

    // Logout and redirect
    if (logout) {
      await logout();
    }

    // Show notification
    showSnackbar('Session expired. Please log in again.', 'warning');

    // Navigate to neutral login
    navigate(routes.login(), {
      state: {
        message: 'Your session has expired due to inactivity',
        from: window.location.pathname,
      },
    });
  }, [navigate, showSnackbar, onSessionExpired, sessionId, logout]);

  const clearScheduledTimeouts = useCallback(() => {
    Object.entries(scheduledTimeoutsRef.current).forEach(([key, timer]) => {
      if (timer) {
        clearTimeout(timer);
        scheduledTimeoutsRef.current[key] = null;
      }
    });
  }, []);

  const startFinalWarningCountdown = useCallback(() => {
    if (!sessionConfigRef.current) return;

    setShowWarning(true);
    setWarningLevel('final');

    clearInterval(warningTimerRef.current);
    warningTimerRef.current = setInterval(() => {
      const newRemaining =
        sessionConfigRef.current.IDLE_TIMEOUT - (Date.now() - lastActivityRef.current);

      if (newRemaining <= 0) {
        handleSessionExpired();
      } else {
        setTimeRemaining(newRemaining);
      }
    }, 1000);
  }, [handleSessionExpired]);

  const scheduleSessionTimers = useCallback(() => {
    if (!sessionConfigRef.current) return;

    clearScheduledTimeouts();

    const now = Date.now();
    const idleTime = now - lastActivityRef.current;
    const remaining = sessionConfigRef.current.IDLE_TIMEOUT - idleTime;

    if (remaining <= 0) {
      handleSessionExpired();
      return;
    }

    setTimeRemaining(remaining);

    scheduledTimeoutsRef.current.expire = setTimeout(() => {
      handleSessionExpired();
    }, remaining);

    const firstThreshold = sessionConfigRef.current.WARNING_TIME;
    const secondThreshold = 2 * 60 * 1000;
    const finalThreshold = sessionConfigRef.current.FINAL_WARNING;

    const firstIn = remaining - firstThreshold;
    if (firstIn > 0) {
      scheduledTimeoutsRef.current.first = setTimeout(() => {
        setWarningLevel('first');
        showSnackbar('Your session will expire in 5 minutes due to inactivity', 'info');
      }, firstIn);
    }

    const secondIn = remaining - secondThreshold;
    if (secondIn > 0) {
      scheduledTimeoutsRef.current.second = setTimeout(() => {
        setWarningLevel('second');
        showSnackbar('Your session will expire in 2 minutes! Please save your work.', 'warning');
      }, secondIn);
    }

    const finalIn = remaining - finalThreshold;
    if (finalIn > 0) {
      scheduledTimeoutsRef.current.final = setTimeout(() => {
        startFinalWarningCountdown();
      }, finalIn);
    } else {
      startFinalWarningCountdown();
    }
  }, [clearScheduledTimeouts, handleSessionExpired, showSnackbar, startFinalWarningCountdown]);

  useEffect(() => {
    scheduleSessionTimersRef.current = scheduleSessionTimers;
  }, [scheduleSessionTimers]);

  // Setup activity tracking (no polling/heartbeats)
  useEffect(() => {
    if (!sessionConfigRef.current || !user) return;

    const activityHandler = updateActivityRef.current || updateActivity;
    sessionConfigRef.current.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, activityHandler);
    });

    // Initialize timers
    scheduleSessionTimers();

    const handleVisibility = () => {
      if (!document.hidden) {
        scheduleSessionTimers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      sessionConfigRef.current?.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, activityHandler);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      clearScheduledTimeouts();
      clearInterval(warningTimerRef.current);
    };
  }, [clearScheduledTimeouts, scheduleSessionTimers, updateActivity, user]);

  // Format time for display
  const formatTime = ms => {
    if (!ms || ms < 0) return '0:00';
    
    const seconds = Math.floor(ms / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds} seconds`;
  };

  // Get appropriate warning message based on user type
  const getWarningMessage = () => {
    const userType = user?.userType || (user?.type === 'patient' ? 'patient' : 'staff');
    const timeout = sessionConfigRef.current?.IDLE_TIMEOUT || 60 * 60 * 1000;
    const minutes = Math.floor(timeout / 60000);
    
    if (userType === 'patient') {
      return `For HIPAA compliance and to protect your medical information, patient sessions automatically expire after ${minutes} minutes of inactivity.`;
    } else {
      return `For security purposes, you will be automatically logged out after ${minutes} minutes of inactivity. This is required for HIPAA compliance to protect patient data.`;
    }
  };

  // Only show dialog for final warning
  if (!showWarning || warningLevel !== 'final') {
    return null;
  }

  return (
    <Dialog
      open={showWarning}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="error" />
        <Typography variant="h6">Session Expiring Soon</Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          Your session will expire due to inactivity in:
        </Alert>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" color="error">
            {formatTime(timeRemaining)}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={(timeRemaining / sessionConfigRef.current?.FINAL_WARNING || 60000) * 100}
            sx={{ mt: 2, height: 8, borderRadius: 4 }}
            color="error"
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {getWarningMessage()}
        </Typography>

        {user?.userType === 'patient' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please save any unsaved work before your session expires.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleSessionExpired} variant="outlined" color="error">
          Log Out Now
        </Button>
        <Button onClick={extendSession} variant="contained" color="primary" autoFocus>
          Continue Working
        </Button>
      </DialogActions>
    </Dialog>
  );
}
